export const EMBEDDED_BROWSER_STATUSES = {
  Idle: 'idle',
  Loading: 'loading',
  Ready: 'ready',
  Error: 'error',
} as const;

export type EmbeddedBrowserStatus =
  (typeof EMBEDDED_BROWSER_STATUSES)[keyof typeof EMBEDDED_BROWSER_STATUSES];

export const EMBED_FAILURE_REASONS = {
  CreateFailed: 'createFailed',
  NotEmbeddable: 'notEmbeddable',
  Aborted: 'aborted',
} as const;

export type EmbedFailureReason =
  (typeof EMBED_FAILURE_REASONS)[keyof typeof EMBED_FAILURE_REASONS];

export interface EmbedFailure {
  readonly reason: EmbedFailureReason;
  readonly message: string | null;
}

export interface EmbeddedBrowserSession {
  readonly sessionId: string;
  readonly embedUrl: string;
  readonly startUrl: string | null;
  readonly expiresAt: number | null;
}

export interface EmbeddedBrowserSessionInput {
  readonly startUrl: string | null;
}

export const EMBED_RENDER_KINDS = {
  Iframe: 'iframe',
  Remote: 'remote',
} as const;

export type EmbedRenderKind =
  (typeof EMBED_RENDER_KINDS)[keyof typeof EMBED_RENDER_KINDS];

interface EmbeddedBrowserPortShared {
  readonly url: string | null;
  readonly status: EmbeddedBrowserStatus;
  readonly error: string | null;
  /**
   * Whether `reload` will actually do something. Status alone cannot answer
   * this: an aborted attempt lands on `Error` but its stored url may belong to
   * a question that has already been torn down, so reloading it is refused.
   */
  readonly canReload: boolean;
  readonly reload: () => void;
}

interface IframeEmbeddedBrowserPort extends EmbeddedBrowserPortShared {
  readonly kind: typeof EMBED_RENDER_KINDS.Iframe;
}

interface RemoteEmbeddedBrowserPort extends EmbeddedBrowserPortShared {
  readonly kind: typeof EMBED_RENDER_KINDS.Remote;
  readonly mount: (element: HTMLElement) => () => void;
}

export type EmbeddedBrowserPort =
  | IframeEmbeddedBrowserPort
  | RemoteEmbeddedBrowserPort;

interface EmbeddedBrowserSessionControls {
  readonly session: EmbeddedBrowserSession | null;
  readonly failure: EmbedFailure | null;
  readonly isCreating: boolean;
  readonly createSession: (
    input: EmbeddedBrowserSessionInput
  ) => Promise<EmbeddedBrowserSession | null>;
  readonly disposeSession: () => Promise<void>;
  readonly abort: () => void;
}

export type EmbeddedBrowserSessionPort = EmbeddedBrowserPort &
  EmbeddedBrowserSessionControls;
